import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Robot from '@/components/Robot';
import ThemeWrapper from '@/components/ThemeWrapper';
import { glowPointerHandlers } from '@/lib/glowPointer';
import { useRobotVoice } from '@/hooks/useRobotVoice';
import { useApp } from '@/context/AppContext';
import { robotTextOrDefault } from '@/lib/robotText';
import { primeSpeechSynthesisFromUserInteraction } from '@/lib/nativeTts';

const ThankYouPage = () => {
  const navigate = useNavigate();
  const { speak, isSpeaking, speechSpectrum } = useRobotVoice();
  const { session, completeSession, settings, fetchSettings } = useApp();
  const completedSessionRef = useRef(null);
  /** Dernière phrase « goodbye » effectivement lue (évite doublon si l’effet se rejoue avec le même texte). */
  const lastSpokenGoodbyeRef = useRef(null);
  const speakInFlightRef = useRef(false);
  /** Après refetch serveur : évite de parler avec des réglages périmés (session longue / prof a modifié entre-temps). */
  const [settingsRefreshed, setSettingsRefreshed] = useState(false);

  useEffect(() => {
    const prime = () => {
      primeSpeechSynthesisFromUserInteraction();
    };
    window.addEventListener('pointerdown', prime, { once: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', prime);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchSettings().finally(() => {
      if (!cancelled) setSettingsRefreshed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchSettings]);

  useEffect(() => {
    if (!settingsRefreshed) return;

    if (session && completedSessionRef.current !== session.id) {
      completedSessionRef.current = session.id;
      completeSession(session.id);
    }

    const goodbyeText = robotTextOrDefault(settings, 'goodbye').trim();
    if (!goodbyeText) return;
    if (lastSpokenGoodbyeRef.current === goodbyeText) return;
    if (speakInFlightRef.current) return;

    speakInFlightRef.current = true;
    void speak(goodbyeText)
      .then((ok) => {
        if (ok) lastSpokenGoodbyeRef.current = goodbyeText;
      })
      .finally(() => {
        speakInFlightRef.current = false;
      });
  }, [settingsRefreshed, session, completeSession, speak, settings]);

  const handleRestart = () => {
    primeSpeechSynthesisFromUserInteraction();
    window.speechSynthesis?.cancel?.();
    navigate('/');
  };

  return (
    <ThemeWrapper className="flex flex-col items-center justify-center p-6 md:p-12 min-h-screen">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center gap-8 md:gap-12 z-10 text-center max-w-xl"
      >
        {/* Robot */}
        <Robot isSpeaking={isSpeaking} speechSpectrum={speechSpectrum} size="large" emotion="happy" />

        {/* Thank you message */}
        <div className="space-y-4">
          <motion.h1
            data-testid="thankyou-title"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-bold text-5xl md:text-7xl tracking-tight text-glow"
          >
            {settings?.ui_texts?.thank_title || 'Merci !'}
          </motion.h1>
          
          <motion.p
            data-testid="thankyou-message"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl font-medium leading-relaxed text-white/80"
          >
            {settings?.ui_texts?.thank_message || 'Tu as été super courageux de me parler.'}
            <br />
            <span className="text-cyan-400">{settings?.ui_texts?.thank_proud || 'Je suis fier de toi !'}</span>
          </motion.p>
        </div>

        {/* Celebration */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="flex items-center gap-4"
        >
          <Sparkles className="w-12 h-12 text-yellow-400" />
          <span className="text-6xl md:text-8xl">🏆</span>
          <Sparkles className="w-12 h-12 text-yellow-400" />
        </motion.div>

        {/* Restart button */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <Button
            data-testid="restart-btn"
            {...glowPointerHandlers}
            onClick={handleRestart}
            className="btn-glow btn-radial-cyan-purple text-white font-bold text-xl md:text-2xl py-6 px-10 md:py-8 md:px-14 rounded-full"
          >
            <Home className="w-6 h-6 mr-2" />
            {settings?.ui_texts?.home_button || 'Retour'}
          </Button>
        </motion.div>
      </motion.div>
    </ThemeWrapper>
  );
};

export default ThankYouPage;
